import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Disable backdrop click + ESC close while a request is in flight, etc. */
  blocking?: boolean;
}

/**
 * Minimal accessible modal. Brand-styled via Tailwind classes that reference
 * the project palette (navy/gold/cream — see docs/06_BRAND_AND_UI.md).
 *
 * Independent of shadcn/ui so it works regardless of which primitives Phase 0
 * installed.
 */
export function SendModalShell({ open, onClose, children, blocking = false }: ModalProps) {
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !blocking) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, blocking, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const root = typeof document !== 'undefined' ? document.body : null;
  if (!root) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto px-0 py-0 sm:items-start sm:px-4 sm:py-12"
      style={{ backgroundColor: 'rgba(13, 43, 74, 0.55)' }}
      onClick={() => {
        if (!blocking) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex w-full max-w-none flex-col bg-white shadow-xl min-h-[100dvh] rounded-none sm:min-h-0 sm:max-w-[640px] sm:rounded-[14px]"
        style={{ border: '1px solid #E8E2D5' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    root,
  );
}
